import React, { useState } from 'react';
import clsx, { ClassValue } from 'clsx';
import MaskInput from 'react-input-mask';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

type Selection = {
  end: number;
  start: number;
  length: number;
};

type MaskedStateType = {
  selection: Selection;
  value: string;
};

type MaskInputType = {
  id?: string;
  value: string;
  disabled?: boolean;
  placeHolder?: string;
  className?: ClassValue;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  type?:
    | 'number'
    | 'search'
    | 'time'
    | 'text'
    | 'email'
    | 'password'
    | 'tel'
    | 'url'
    | 'date'
    | undefined;
  pattern?: string;
  onDrop?: any;
  min?: string;
  isPhnNumber?: boolean;
};

const maskCharCountForUs = 12; //no of alpahanumeric char in a phone number.
const maskCharCountForOthers = 15; //start index of alphabets inside phone number

function InputMask({
  id,
  value,
  disabled,
  className,
  type,
  onChange,
  pattern,
  onBlur,
  onDrop,
  min,
  isPhnNumber,
}: MaskInputType) {
  const [showPhnValidation, setShowPhnValidation] = useState(false);

  //To restrict removing characters from starting of input
  const beforeMaskedValueChange = (
    newState: MaskedStateType,
    oldState: MaskedStateType,
    inputValue: any,
  ): MaskInputType => {
    const maxMaskCharCount =
      pattern === '999999999999999'
        ? maskCharCountForOthers
        : pattern === '999-999-9999 **********' && isPhnNumber === true
        ? maskCharCountForUs + 11
        : maskCharCountForUs;

    if (inputValue) {
      let userInput =
        isPhnNumber === true
          ? inputValue?.replace(/[^a-zA-Z0-9]/g, '')
          : inputValue?.replace(/[^0-9]/g, '');
      let { value, selection } = oldState;
      newState = {
        ...newState,
        value:
          value.length < maxMaskCharCount
            ? copyPasteString(
                value,
                selection.start,
                userInput,
                selection.length,
              )
            : oldState.value,
      };
    }

    return {
      ...newState,
      value:
        pattern === '999999999999999'
          ? newState.value.replace(/([^\w ]|_)/g, '').replace(/ /g, '')
          : formatPhnumber(newState.value),
    };
  };

  const formatPhnumber = (phnnumber: string) => {
    if (isPhnNumber === true && pattern === '999-999-9999 **********') {
      const spaceIndex = phnnumber.indexOf(' ');
      let firstPart = phnnumber;
      let secondPart = '';
      
      if (spaceIndex !== -1) {
        firstPart = phnnumber.substring(0, spaceIndex);
        secondPart = phnnumber.substring(spaceIndex + 1);
      }
      
      const cleanFirstPart = firstPart.replace(/[^0-9]/g, '').slice(0, 10);
      
      let formatted = '';
      for (let i = 0; i < cleanFirstPart.length; i++) {
        if (i === 3 || i === 6) {
          formatted += '-';
        }
        formatted += cleanFirstPart[i];
      }
      
      if (secondPart) {
        const cleanSecondPart = secondPart.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10);
        formatted += ' ' + cleanSecondPart;
      }
      
      return formatted;
    } else {
      let formattedStr: string = '';
      const phnNumber = phnnumber.replace(/([^\w ]|_)/g, '').replace(/ /g, '');
      const digitCount =
        pattern === '999999999999999'
          ? maskCharCountForOthers
          : maskCharCountForUs - 2;

      for (let i = 0; i < phnNumber.length && i < digitCount; i++) {
        if (
          (i == 3 && phnNumber.length >= 3) ||
          (i == 6 && phnNumber.length >= 7)
        ) {
          formattedStr += '-';
        }
        formattedStr += phnNumber[i];
      }
      return formattedStr;
    }
  };

  const copyPasteString = (
    str: string,
    start: number,
    input: string,
    length: number,
  ): string => {
    return str.substring(0, start) + input + str.substring(start + length);
  };

  const userClasses = clsx(className);

  return (
    <>
      <MaskInput
        id={id}
        mask={pattern}
        type={type}
        min={min}
        disabled={disabled}
        className={userClasses}
        value={value}
        onChange={(e: any) => {
          onChange && onChange(e);
        }}
        onBlur={(e: any) => {
          if (isPhnNumber && pattern === '999-999-9999 **********') {
            const cleaned = e.target.value.replace(/[^0-9]/g, '');
            setShowPhnValidation(cleaned.length < 10);
          }
          onBlur && onBlur(e);
        }}
        onDrop={onDrop}
        onDragOver={(e: any) => e.preventDefault()}
        beforeMaskedValueChange={
          isPhnNumber && isPhnNumber === true ? beforeMaskedValueChange : null
        }
      ></MaskInput>
      {showPhnValidation && (
        <p className="mt-1 inline-flex items-start gap-1 text-xs text-red-600 dark:text-red-400 md:text-sm">
          <ExclamationTriangleIcon
            className="relative -bottom-[2px] -ml-0.5 h-5 w-5"
            aria-hidden="true"
          />
          Please enter a valid phone number (e.g., xxx-xxx-xxxx)
        </p>
      )}
    </>
  );
}

export { InputMask };
